import { Card, Stack, styled, TextField } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import { PromptGroup } from '@server/prompts';
import pluralize from 'pluralize';
import { DotBullet } from '@sweep-io/sweep-design/dist/icons';
import { useRef, useEffect, useMemo } from 'react';
import { PromptCardActions } from './PromptCardActions';
import { CardIcon } from './CardIcon';
import { CardPin } from './CardPin';
import { CardLoader } from './CardLoader';
import { usePromptGroups } from '../usePromptGroups';
import { usePromptLibraryCard } from './usePromptLibraryCard';
import { useSelector } from 'react-redux';
import { selectPromptGroups } from '../../../../reducers/promptLibraryReducer';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { ClampedText } from '../../../common/ClampedText';
import { TruncatedTextTooltip } from '../../../common/TruncatedTextTooltip';

interface PromptGroupCardContentProps {
  promptGroup: PromptGroup;
  onClick?: (promptGroup: PromptGroup) => void;
}

export const PromptGroupCardContent = ({ promptGroup, onClick }: PromptGroupCardContentProps) => {
  const { addNotification } = useSweepNotifications();
  const allPromptGroups = useSelector(selectPromptGroups);
  const pinnedGroups = useMemo(
    () => allPromptGroups?.filter((group) => group.isPinned).map((group) => group.id),
    [allPromptGroups],
  );

  const {
    promptGroupInEdit,
    showNameErrorState,
    onEdit,
    onConfirm,
    setPromptGroupInEdit,
    onDeleteCard,
    isCardLoading,
  } = usePromptLibraryCard({
    promptGroup,
  });

  const isEditable = !!promptGroupInEdit;
  const { pinGroup } = usePromptGroups();
  const _promptGroup = promptGroupInEdit ?? promptGroup;
  const { iconName, name, description, prompts } = _promptGroup;
  const nonEmptyPrompts = prompts?.filter((prompt) => prompt.id !== '');

  const cardRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onPinClick = (groupId: string, isPinned: boolean) => {
    if (!isPinned) {
      const groupsLeft = pinnedGroups?.filter((group) => group !== groupId);
      if (groupsLeft?.length === 0) {
        addNotification({
          message: 'There should be at least one pinned prompt category',
          variant: SweepNotificationVariant.Error,
        });
        return;
      }
    }
    pinGroup(groupId, isPinned);
  };

  const handleCardBlur = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    // Set a short timeout to check if focus moved within the card
    blurTimeoutRef.current = setTimeout(() => {
      const activeElement = document.activeElement;
      const cardElement = cardRef.current;

      // Only confirm if focus moved outside the card
      if (cardElement && !cardElement.contains(activeElement)) {
        onConfirm?.(_promptGroup);
      }
    }, 0);
  };

  const handleCardFocus = () => {
    // Clear timeout if focus returns to the card
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setPromptGroupInEdit(null);
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      onConfirm?.(_promptGroup);
    }
  };

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const renderCardTitle = () => {
    if (isEditable) {
      return (
        <TextField
          value={name}
          onChange={(e) => {
            if (e.target.value.length <= 150) {
              onEdit('name', e.target.value);
            }
          }}
          onKeyDown={handleKeyDown}
          autoFocus
          placeholder="Add title"
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: colors.grey[100],
              minHeight: '32px',
              paddingRight: '6px',
            },

            '& fieldset': {
              borderColor: showNameErrorState ? colors.blush[500] + '!important' : undefined,
            },
          }}
          InputProps={{
            endAdornment: (
              <Stack direction="row" alignItems="center" sx={{ svg: { pl: 0.5 } }}>
                <Typography variant="tiny" whiteSpace="nowrap" color={colors.grey[500]}>
                  Max 150 characters
                </Typography>
                <DotBullet color={colors.blush[500]} />
              </Stack>
            ),
          }}
        />
      );
    }
    return <TruncatedTextTooltip variant="body-medium">{name}</TruncatedTextTooltip>;
  };

  const renderCardDescription = () => {
    if (isEditable) {
      return (
        <TextField
          value={description}
          onChange={(e) => onEdit('description', e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add description"
          sx={{
            mt: 0.5,
            '& .MuiInputBase-root': {
              backgroundColor: colors.grey[100],
            },
          }}
        />
      );
    }

    return (
      <ClampedText
        text={description ? description : 'Add description'}
        lines={3}
        typographyVariant="body"
        textColor={colors.grey[700]}
      />
    );
  };

  return (
    <StyledCard
      ref={cardRef}
      sx={{
        flex: 1,
        borderRadius: '8px',
        width: '100%',
        p: 2,
        position: 'relative',
        cursor: isEditable || isCardLoading ? 'default' : 'pointer',

        '&:hover .prompt-card-actions': {
          display: 'flex',
        },
      }}
      onClick={() => !isEditable && !isCardLoading && onClick?.(_promptGroup)}
      onBlur={isEditable && !isCardLoading ? handleCardBlur : undefined}
      onFocus={isEditable && !isCardLoading ? handleCardFocus : undefined}
    >
      {isCardLoading && <CardLoader />}

      <Stack direction="row" alignItems="center" justifyContent="space-between" pb={1}>
        <CardIcon icon={iconName} />

        <Stack direction="row" alignItems="center" gap={1}>
          <PromptCardActions
            onEdit={() => setPromptGroupInEdit(_promptGroup)}
            onDelete={() => onDeleteCard?.(_promptGroup)}
            isDisabled={!!isCardLoading}
          />

          <CardPin
            isPinned={!!_promptGroup.isPinned}
            groupId={_promptGroup.id}
            onPinClick={onPinClick}
            isCardLoading={!!isCardLoading}
          />

          <Stack
            sx={{
              backgroundColor: colors.grey[150],
              borderRadius: '50px',
              p: '2px 6px',
            }}
          >
            <Typography variant="caption" color={colors.grey[800]}>
              {nonEmptyPrompts.length} {pluralize('Prompt', nonEmptyPrompts.length)}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* pre-line so new line (/n) can be rendered properly */}
      <Stack gap={0.5} sx={{ whiteSpace: 'pre-line' }}>
        {renderCardTitle()}
        {renderCardDescription()}
      </Stack>
    </StyledCard>
  );
};

export const StyledCard = styled(Card)({
  border: '1px solid',
  borderColor: colors.grey[300],
  borderRadius: '4px',
  padding: '8px 16px 8px 12px',
  cursor: 'pointer',
  gap: '4px',
  boxShadow: 'none',
});
